import { useCallback } from 'react';
import * as React from 'react';
import { Pressable } from 'react-native';
import { useTranslation } from 'react-i18next';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { Toast, OverflowMenu, MenuItem, useConfirm } from '@pletly/ui';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useBoolean } from 'usehooks-ts';

import { getName } from '@pletly/shared';
import { useOnScroll } from '@pletly/common';
import { useUser } from '%hooks';
import { HamburgerIcon } from '%components/icons/Hamburger';
import { GetInformationFeedDataDocument } from '%screens/profile/InformationFeed/__generated__/feed.generated';
import { CommunityGroupDocument } from '%screens/communities/components/__generated__/group.generated';
import { useDeletePost } from '%screens/posts/components/useDeletePost';
import { RootStackParamList } from '%navigation/types';
import { PostType } from '%__generated__/types';
import { GetCurrentUserPostsDocument } from '%context/Posts/__generated__/userPosts.generated';
import { PostData } from '../types';
import { useTogglePinPost } from '../useTogglePinPost';
import { useReportPostMutation } from './__generated__/reportPost.mutation.generated';
import { useBlockUserMutation } from './__generated__/blockUser.mutation.generated';

interface Props {
	post: PostData;
	allowPin?: boolean;
	disabled?: boolean;
	onFeed?: boolean;
}

const isInformationFeedPostParams = (
	params: object,
): params is RootStackParamList['InformationFeedPost'] =>
	['userId', 'folderId', 'feedId'].every((key) => key in params);
export const PostMenu = ({ post, allowPin = false, disabled, onFeed }: Props) => {
	const { t } = useTranslation();
	const { navigate, replace } = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
	const { path, params } =
		useRoute<RouteProp<RootStackParamList, 'PostComments' | 'InformationFeedPost'>>();
	const isCommunityPost = path?.includes('/communities/') || params?.uuid || params?.communityUuid;
	const isInformationPost = post.type === PostType.Information;
	const isOnPostComments = !onFeed;

	const { value: isMenuOpen, setTrue: openMenu, setFalse: closeMenu } = useBoolean();
	const user = useUser();
	const { confirm } = useConfirm();

	const [reportPost] = useReportPostMutation({
		variables: { postId: post.id },
		refetchQueries: [GetCurrentUserPostsDocument],
	});
	const [blockUser] = useBlockUserMutation({
		variables: { userId: post.author?.id || '' },
		refetchQueries: [GetCurrentUserPostsDocument],
	});

	useOnScroll(closeMenu);
	const isCurrentUserPost = user?.id.toString() === post.author.id;
	const { togglePinPost } = useTogglePinPost({ postId: post.id, isPinned: post.pinned });
	const hideOptionsForPal = user.role === 'USER' && user.restrictedAccess;

	const handleTogglePinPost = async () => {
		closeMenu();
		try {
			await togglePinPost();
		} catch (error) {
			console.error(error);
		}
	};

	const handleReportPost = async () => {
		const confirmation = await confirm(
			t('post.are-you-sure-to-report-this-post'),
			t('post.this-will-hide-it-for-you-and-report-the-post-to-the-moderators'),
		);
		if (!confirmation) {
			return;
		}

		closeMenu();
		try {
			await reportPost();
			Toast.show({
				type: 'success',
				text1: t('post.report-sent'),
				text2: t('post.we-will-review-your-report-shortly'),
			});
		} catch (error) {
			console.error(error);
		}
	};

	const handleBlockUser = async () => {
		const confirmation = await confirm(
			t('post.are-you-sure-to-block-author-user', {
				user: getName(post.author),
			}),
			t('post.you-will-not-see-content-from-this-user-anymore'),
		);
		if (!confirmation) {
			return;
		}

		closeMenu();
		try {
			await blockUser();
			Toast.show({
				type: 'success',
				text1: t('post.user-blocked'),
			});
		} catch (error) {
			console.error(error);
		}
	};

	const renderToggleButton = useCallback(
		() => (
			<Pressable
				testID="post-menu-button"
				onPress={openMenu}
				disabled={disabled}
				// @ts-expect-error valid for web
				dataSet={{
					includeTransformsInMeasurments: true,
				}}
			>
				<HamburgerIcon />
			</Pressable>
		),
		[openMenu, disabled],
	);

	const navigatebackFromPost = useCallback(() => {
		if (!isOnPostComments) {
			return;
		}

		if (isCommunityPost && params.communityUuid) {
			return replace('Community', {
				screen: 'CommunityView',
				params: { uuid: params.communityUuid },
			});
		}

		if (isInformationPost && post.informationFeedId && isInformationFeedPostParams(params)) {
			return replace('InformationFeed', {
				feedId: post.informationFeedId,
				folderId: params.folderId,
				userId: params.userId,
			});
		}

		return replace('Home');
	}, [
		isOnPostComments,
		isCommunityPost,
		isInformationPost,
		post.informationFeedId,
		replace,
		params,
	]);

	const editPost = () => {
		closeMenu();
		if (isCommunityPost) {
			return navigate('Community', {
				screen: 'EditCommunityPost',
				params: {
					uuid: params.uuid,
					postUuid: post.uuid,
				},
			});
		}

		if (isInformationPost && post.informationFeedId && isInformationFeedPostParams(params)) {
			return navigate('EditInformationFeedPost', {
				folderId: params.folderId,
				userId: params.userId,
				feedId: params.feedId || post.informationFeedId,
				postUuid: post.uuid,
			});
		}

		navigate('EditPost', {
			postUuid: post.uuid,
		});
	};

	const deletePost = useDeletePost({
		postId: post.id,
		onSuccess: navigatebackFromPost,
		refetchQueries: [
			isInformationPost
				? isCommunityPost
					? CommunityGroupDocument
					: GetInformationFeedDataDocument
				: GetCurrentUserPostsDocument,
		],
	});
	const handleDeletePost = useCallback(() => {
		closeMenu();

		return deletePost();
	}, [closeMenu, deletePost]);

	const openSharePost = useCallback(() => {
		closeMenu();

		return navigate('SharePost', { postUuid: post.uuid });
	}, [closeMenu, navigate, post.uuid]);

	const menuItems = [
		{
			item: <MenuItem title={t('common.edit')} onPress={editPost} />,
			visible: isCurrentUserPost && !hideOptionsForPal,
		},
		{
			item: (
				<MenuItem
					title={!post.pinned ? t('post.pin-post') : t('post.unpin-post')}
					onPress={handleTogglePinPost}
				/>
			),
			visible: allowPin,
		},
		{
			item: <MenuItem title={t('post.share.share')} onPress={openSharePost} />,
			visible: !hideOptionsForPal,
		},
		{
			item: <MenuItem title={t('post.delete-post')} status="danger" onPress={handleDeletePost} />,
			visible: isCurrentUserPost && !hideOptionsForPal,
		},
		{
			item: <MenuItem title={t('post.report-post')} onPress={handleReportPost} />,
			visible: !isCurrentUserPost && !hideOptionsForPal && post.type !== PostType.Organisation,
		},
		{
			item: <MenuItem title={t('post.block-user')} onPress={handleBlockUser} />,
			visible: !isCurrentUserPost && !hideOptionsForPal && post.type !== PostType.Organisation,
		},
	];

	return (
		<OverflowMenu
			anchor={renderToggleButton}
			visible={isMenuOpen}
			placement="bottom end"
			onBackdropPress={closeMenu}
		>
			{menuItems.reduce((itemsToShow, { item, visible }, index) => {
				if (visible) {
					itemsToShow.push({ ...item, key: `${index}` });
				}

				return itemsToShow;
			}, [] as React.ReactElement[])}
		</OverflowMenu>
	);
};
